import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment as ENV } from 'src/environments/environment';
import { ChatStreamEvent } from '../models/chat-message';
import { AppHttpService } from './app-http.service';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class ChatService {

  constructor(
    private http: AppHttpService,
    private authService: AuthService,
  ) {}

  /**
   * Send a message and receive SSE stream.
   * Uses fetch() because Angular HttpClient doesn't support streaming.
   */
  sendMessage(projectId: string, question: string, conversationId?: string): Observable<ChatStreamEvent> {
    return new Observable(observer => {
      const url = `${ENV.API_ENDPOINT}/v1/chat/${projectId}/message`;

      // Get the token the same way the auth interceptor does
      const user = this.authService.getCurrentUser();
      const token = user?.token || '';

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'token': token,
        },
        body: JSON.stringify({ question, conversation_id: conversationId || null }),
      })
        .then(async response => {
          if (!response.ok) {
            // Parse error body for specific error codes
            try {
              const errorBody = await response.json();
              if (response.status === 403 && errorBody.code === 'AI_CONSENT_REQUIRED') {
                observer.error({ status: 403, code: 'AI_CONSENT_REQUIRED', message: errorBody.message });
                return;
              }
              if (response.status === 429) {
                observer.error({ status: 429, retryAfter: errorBody.retry_after, message: errorBody.message });
                return;
              }
            } catch (e) {
              // Body wasn't JSON, fall through
            }
            throw new Error(`HTTP ${response.status}`);
          }

          const reader = response.body!.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          const processStream = async () => {
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                observer.complete();
                break;
              }

              buffer += decoder.decode(value, { stream: true });

              // Parse SSE events from buffer
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // Keep incomplete line in buffer

              let currentEvent = '';
              for (const line of lines) {
                if (line.startsWith('event: ')) {
                  currentEvent = line.substring(7);
                } else if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.substring(6));
                    observer.next({ event: currentEvent as any, data });
                  } catch (e) {
                    // Skip malformed JSON
                  }
                }
              }
            }
          };

          processStream().catch(err => observer.error(err));
        })
        .catch(err => observer.error(err));
    });
  }

  /**
   * Check if the current user has accepted AI data processing terms.
   */
  getConsent(): Observable<any> {
    return this.http.getService('/v1/chat/consent');
  }

  /**
   * Accept the AI data processing terms.
   */
  acceptConsent(): Observable<any> {
    return this.http.postService('/v1/chat/consent', {});
  }

  /**
   * Get conversations for a project (uses standard HTTP).
   */
  getConversations(projectId: string): Observable<any> {
    return this.http.getService(`/v1/chat/${projectId}/conversations`);
  }

  /**
   * Get messages for a conversation.
   */
  getMessages(projectId: string, conversationId: string): Observable<any> {
    return this.http.getService(`/v1/chat/${projectId}/conversations/${conversationId}/messages`);
  }

  /**
   * Delete a conversation.
   */
  deleteConversation(projectId: string, conversationId: string): Observable<any> {
    return this.http.deleteService(`/v1/chat/${projectId}/conversations/${conversationId}`);
  }

  /**
   * Get PDF download URL for a message.
   */
  getPdfUrl(projectId: string, messageId: string): string {
    return `${ENV.API_ENDPOINT}/v1/chat/${projectId}/messages/${messageId}/pdf`;
  }

  /**
   * Toggle favorite status on a conversation.
   */
  toggleFavorite(projectId: string, conversationId: string): Observable<any> {
    return this.http.putService(`/v1/chat/${projectId}/conversations/${conversationId}/favorite`, {});
  }

  /**
   * Get favorite conversations for a project.
   */
  getFavorites(projectId: string): Observable<any> {
    return this.http.getService(`/v1/chat/${projectId}/conversations/favorites`);
  }
}
